import type { Organization, Patient, Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  DocumentCategory,
} from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import {
  BEHAVIORAL_HEALTH_FOLDER_ID,
  type Folder,
} from '@seen/core/models/document-references/folder'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import type { Signature } from '@seen/core/models/signature'
import { getReferenceString, resolveId } from '@seen/core/references'
import type { QueryTypes } from '@seen/core/search'
import { isOfType } from '@seen/core/utils'
import {
  Badge,
  Button,
  cn,
  ResponsiveTabs,
  ResponsiveTabsList,
  TabsTrigger,
} from '@seen/ui'
import {
  IconCategory,
  IconChevronDown,
  IconEye,
  IconFolderPlus,
  IconPencil,
  IconUser,
} from '@tabler/icons-react'
import type {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { countBy, uniqBy } from 'lodash-es'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import {
  getSortableColumns,
  SearchSortChip,
} from '#src/components/core/data-table/SearchSortChip.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  HoverOrganizationContent,
  HoverPractitionerAvatar,
} from '#src/components/core/hover-card/index.ts'
import { ShortcutSearchInput } from '#src/components/core/input/ShortcutSearchInput.tsx'
import { Logo } from '#src/components/core/Logo.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import {
  DateDisplay,
  DateTimeDisplay,
} from '#src/components/display/DateTimeDisplay.tsx'
import { DocumentInfoCell } from '#src/components/document-reference/DocumentInfoCell.tsx'
import { DocumentReferenceSheet } from '#src/components/document-reference/DocumentReferenceSheet.tsx'
import { DocumentReviewTaskBadge } from '#src/components/document-reference/DocumentReviewTaskBadge.tsx'
import {
  DOCUMENT_SEARCH_OPTIONS,
  extractDocSearchFields,
} from '#src/components/document-reference/documentSearchFields.ts'
import { useFolderAccess } from '#src/components/document-reference/useFolderAccess.ts'
import { useFolders } from '#src/components/document-reference/useFolders.ts'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import {
  FilterValue,
  useFilteredData,
} from '#src/components/filter/useFilteredData.ts'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { useClientSideSearch } from '#src/hooks/useClientSideSearch.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDocumentReviewTasks } from '#src/hooks/useDocumentReviewStatus.ts'
import { useDocumentSignatureStatus } from '#src/hooks/useDocumentSignatureStatus.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useBatchMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'
import { useUrlSearchInput } from '#src/hooks/useUrlSearchInput.ts'

// Document status values for sorting. Lower values sort first when ascending.
const STATUS_NEEDS_REVIEW = 0
const STATUS_SIGNED = 1
const STATUS_NONE = 2

const COLUMN_WIDTHS = {
  select: 40,
  status: 96,
  author: 64,
  serviceDate: 88,
  date: 80,
} as const

const READABLE_TITLE_COLUMN_WIDTHS = {
  select: 40,
  status: 80,
  author: 56,
  serviceDate: 80,
  date: 76,
} as const

/* URL keys that round-trip the active sort. With `searchKeepsTableSort` off,
 * a missing `sortBy` while searching means relevance. With it on, missing
 * means the table default and `sortBy=relevance` is the explicit relevance
 * order. */
const SORT_BY_PARAM = 'sortBy'
const SORT_DIR_PARAM = 'sortDir'
const RELEVANCE_SORT_BY = 'relevance'

/* Applied when no `?sortBy=` is set, and — when `searchKeepsTableSort` is
 * on — when a search has no explicit sort either. */
const DEFAULT_SORTING: SortingState = [{ id: 'date', desc: true }]

export function DocumentReferencesTable({
  patient,
}: {
  patient: Reference<Patient>
}): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const folders = useFolders()
  /* Hide folder tabs the user has no role for. Frontend-only filter;
   * backend AccessPolicy is the real gate (followup). */
  const { visibleFolders, hiddenFolderIds } = useFolderAccess()
  const canAccessBehavioralHealth = visibleFolders.some(
    (f) => f.id === BEHAVIORAL_HEALTH_FOLDER_ID
  )
  const [filters, setFilters] = useState<Filter[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedFolderId = searchParams.get('folder') ?? undefined
  const selectedFolder = folders.find((f) => f.id === selectedFolderId)
  const selectedCategory = (searchParams.get('category') ?? undefined) as
    | DocumentCategory
    | undefined
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const keepTableSort = useFeatureFlag(featureFlags.searchKeepsTableSort)
  const readableTitles = useFeatureFlag(
    featureFlags.documentsTableReadableTitles
  )
  const columnWidths = readableTitles
    ? READABLE_TITLE_COLUMN_WIDTHS
    : COLUMN_WIDTHS
  const batchMutation = useBatchMutation()

  /* React Router's setSearchParams rebuilds from the render's closure, so two
   * writes in one event (a sort, then DataTable's page reset) would clobber
   * each other. Threading both through this ref makes the second write build
   * on the first. */
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const updateSearchParams = useCallback(
    (mutate: (params: URLSearchParams) => void): void => {
      const next = new URLSearchParams(searchParamsRef.current)
      mutate(next)
      searchParamsRef.current = next
      setSearchParams(next)
    },
    [setSearchParams]
  )

  const documentReferenceId =
    searchParams.get('documentReferenceId') ?? undefined
  const pageSize = Number(searchParams.get('pageSize') ?? 20)
  const pageIndex = Number(searchParams.get('pageIndex') ?? 0)
  const {
    searchInput: inputValue,
    setSearchInput: setInputValue,
    debouncedSearch,
  } = useUrlSearchInput({
    debounceMs: 1000,
    // Each query is its own back-navigable step on this surface.
    replace: false,
    onDebouncedChange: (params) => {
      /* Flag off, a new query re-ranks from relevance, and a cleared query
       * unmounts the chip, so leftover sort params would be invisible and
       * inherited by the next search. Flag on, the table's sort survives. */
      if (!keepTableSort) {
        params.delete(SORT_BY_PARAM)
        params.delete(SORT_DIR_PARAM)
      }
      params.set('pageIndex', '0')
    },
  })

  /* If a user without BH access lands on a BH folder URL (deep link or
   * stale tab), redirect to the All tab. */
  useEffect(() => {
    if (
      selectedFolderId === BEHAVIORAL_HEALTH_FOLDER_ID &&
      !canAccessBehavioralHealth
    ) {
      setSearchParams((prev) => {
        prev.delete('folder')
        prev.delete('documentReferenceId')
        prev.set('pageIndex', '0')
        return prev
      })
    }
  }, [selectedFolderId, canAccessBehavioralHealth, setSearchParams])

  const labCopyGrouping = useFeatureFlag(featureFlags.labCopyGrouping)
  /* Flag on, a result's copies show as one row; the sidebar lists the rest.
   * Flag off, a Quest PDF the reconciler filed to this participant stays out,
   * so the table is what it was before the feature. */
  const labCopyView = (query: QueryTypes): QueryTypes =>
    labCopyGrouping
      ? ExtendedDocumentReference.excludingLabCopySecondaries(query)
      : ExtendedDocumentReference.excludingLabCopyFiled(query)

  const allDocumentsQuery = useSearchResourcePages(
    ExtendedDocumentReference,
    labCopyView(
      ExtendedDocumentReference.byParticipantExcludingFolders(patient)
    )
  )

  /* Don't even request BH docs for non-authorized users — passing
   * `undefined` skips the query entirely so the response never lands in
   * the browser cache. */
  const behavioralHealthQuery = useSearchResourcePages(
    ExtendedDocumentReference,
    canAccessBehavioralHealth
      ? labCopyView(
          ExtendedDocumentReference.byParticipantInBehavioralHealth(patient)
        )
      : undefined
  )

  /* On the All tab, merge BH docs in for authorized users so review,
   * signed, and category counts reflect them. The `canAccessBehavioralHealth`
   * gate on the BH-tab branch is defense-in-depth — the real gate is
   * the backend AccessPolicy. */
  const activeDocuments = useMemo(() => {
    if (
      selectedFolderId === BEHAVIORAL_HEALTH_FOLDER_ID &&
      canAccessBehavioralHealth
    ) {
      return behavioralHealthQuery.data
    }
    const all = allDocumentsQuery.data
    if (canAccessBehavioralHealth) {
      const bh = behavioralHealthQuery.data
      if (!all && !bh) {
        return undefined
      }
      return [...(all ?? []), ...(bh ?? [])]
    }
    return all?.filter((doc) => doc.folder?.id !== BEHAVIORAL_HEALTH_FOLDER_ID)
  }, [
    selectedFolderId,
    allDocumentsQuery.data,
    behavioralHealthQuery.data,
    canAccessBehavioralHealth,
  ])

  const listedDocument = activeDocuments?.find(
    (doc) => doc.id === documentReferenceId
  )

  /* The sidebar's "Other copies" links open a copy the table leaves out. Only
   * such a copy is read on its own; any other id missing from the list stays
   * closed, as before. */
  const { data: unlistedCopy } = useSearchOne(
    ExtendedDocumentReference,
    labCopyGrouping && activeDocuments && !listedDocument && documentReferenceId
      ? ExtendedDocumentReference.excludingFolders(
          {
            ...ExtendedDocumentReference.byParticipant({
              participant: patient,
            }),
            _id: documentReferenceId,
          },
          hiddenFolderIds
        )
      : undefined
  )
  const selectedDocument =
    listedDocument ??
    (unlistedCopy?.isLabCopySecondary &&
    unlistedCopy.subject?.reference === patient.reference
      ? unlistedCopy
      : undefined)

  /* Union of every doc that could surface across tabs — feeds the
   * review-task, signature, and category-count hooks. Identical to the
   * excluding-folders result for non-authorized users. */
  const statusSourceDocuments = useMemo(() => {
    const all = allDocumentsQuery.data ?? []
    if (!canAccessBehavioralHealth) {
      return all
    }
    return [...all, ...(behavioralHealthQuery.data ?? [])]
  }, [
    allDocumentsQuery.data,
    behavioralHealthQuery.data,
    canAccessBehavioralHealth,
  ])

  const {
    tasksByDocument: reviewTasksByDocument,
    allTasks: allReviewTasks,
    taskDocumentFolders,
  } = useDocumentReviewTasks(statusSourceDocuments)
  const taskedForReview = useMemo(
    () => new Set(reviewTasksByDocument.keys()),
    [reviewTasksByDocument]
  )

  const signedDocuments = useDocumentSignatureStatus(statusSourceDocuments)

  // Precompute status priority so sort comparators avoid map lookups + getReferenceString per compare.
  const documentStatusPriority = useMemo(() => {
    const statusMap = new Map<string, number>()
    for (const doc of statusSourceDocuments) {
      const refString = getReferenceString(doc.reference)
      if (reviewTasksByDocument.has(refString)) {
        statusMap.set(doc.id, STATUS_NEEDS_REVIEW)
      } else if (signedDocuments.has(refString)) {
        statusMap.set(doc.id, STATUS_SIGNED)
      } else {
        statusMap.set(doc.id, STATUS_NONE)
      }
    }
    return statusMap
  }, [statusSourceDocuments, reviewTasksByDocument, signedDocuments])

  const { categoriesWithCounts, categoryReviewCounts } = useMemo(() => {
    const categoryCounts = countBy(
      statusSourceDocuments.flatMap((doc) => doc.categories),
      (category) => category
    )

    const categoryReviewCounts = countBy(
      statusSourceDocuments
        .filter((doc) => taskedForReview.has(getReferenceString(doc.reference)))
        .flatMap((doc) => doc.categories),
      (category) => category
    )

    const pinnedCategories = new Set<DocumentCategory>([
      DocumentCategory.IMAGING,
      DocumentCategory.LABS,
    ])

    const categoriesWithCounts = DOCUMENT_CATEGORY_OPTIONS.map((opt) => ({
      ...opt,
      count: categoryCounts[opt.value] ?? 0,
    })).filter((opt) => opt.count > 0 || pinnedCategories.has(opt.value))

    return { categoriesWithCounts, categoryReviewCounts }
  }, [statusSourceDocuments, taskedForReview])

  // Calculate review task counts per folder (for tasks NOT assigned to current user)
  const folderReviewCounts = useMemo(() => {
    const tasksNotAssignedToMe = allReviewTasks.filter((task) => {
      const docId = resolveId(task.document)
      return (
        task.ownerPractitioner?.reference !==
          practitioner.reference.reference && !!docId
      )
    })

    const knownFolderIds = new Set(folders.map((f) => f.id))
    return countBy(tasksNotAssignedToMe, (task) => {
      // oxlint-disable-next-line typescript/no-non-null-assertion -- docId guaranteed by !!docId filter above
      const docId = resolveId(task.document)!
      const folderId = taskDocumentFolders.get(docId)
      // Consolidate unknown folder IDs into 'other'
      if (!folderId || !knownFolderIds.has(folderId)) {
        return 'other'
      }
      return folderId
    })
  }, [
    allReviewTasks,
    taskDocumentFolders,
    folders,
    practitioner.reference.reference,
  ])

  const canAccessSelectedFolder =
    !selectedFolder || practitioner.isRole(...selectedFolder.roles)

  const maybeShowDocumentReference = useCallback(
    (documentReference: ExtendedDocumentReference): void => {
      if (!canAccessSelectedFolder) {
        toast.error('You are not authorized to access documents in this folder')
        return
      }
      setSearchParams((prev) => {
        prev.set('documentReferenceId', documentReference.id)
        return prev
      })
    },
    [canAccessSelectedFolder, setSearchParams]
  )

  const authorOptions = useMemo(
    () =>
      uniqBy(
        activeDocuments
          ?.filter((doc) => doc.author?.reference)
          .map((doc) => {
            const authorPractitioner = doc.authorPractitioner
            return {
              label: authorPractitioner ? (
                <PractitionerOption practitioner={authorPractitioner} />
              ) : (
                (referenceDisplay(doc.author) ?? '')
              ),
              icon: authorPractitioner ? (
                <Avatar
                  profile={authorPractitioner}
                  size="icon"
                  className="size-4"
                />
              ) : null,
              value: doc.author?.reference ?? '',
              keywords: [referenceDisplay(doc.author) ?? ''],
            }
          }) ?? [],
        'value'
      ),
    [activeDocuments]
  )

  const filterOptions = useMemo<FilterConfigMap<ExtendedDocumentReference>>(
    () => ({
      reviewTask: {
        label: 'Review Task',
        icon: <IconEye className="size-4" />,
        type: 'multi-select',
        options: [
          {
            label: 'Tasked for review',
            value: 'tasked',
            keywords: ['tasked', 'review'],
          },
          {
            label: 'No review task',
            value: 'not-tasked',
            keywords: ['no', 'task'],
          },
        ],
        valueFn: (doc) => {
          const hasTask = taskedForReview.has(getReferenceString(doc.reference))
          return hasTask ? 'tasked' : 'not-tasked'
        },
      },
      signatureStatus: {
        label: 'Signature',
        icon: <IconPencil className="size-4" />,
        type: 'multi-select',
        options: [
          { label: 'Signed', value: 'signed', keywords: ['signed'] },
          {
            label: 'No signature',
            value: 'no-signature',
            keywords: ['no', 'unsigned'],
          },
        ],
        valueFn: (doc) => {
          if (!doc.subject) {
            return 'n/a'
          }
          const isSigned = signedDocuments.has(
            getReferenceString(doc.reference)
          )
          return isSigned ? 'signed' : 'no-signature'
        },
      },
      category: {
        label: 'Category',
        icon: <IconCategory className="size-4" />,
        type: 'multi-select-array',
        options: [
          {
            value: FilterValue.None,
            label: 'No category',
            keywords: ['no category', 'uncategorized'],
          },
          ...DOCUMENT_CATEGORY_OPTIONS.map((o) => ({
            ...o,
            keywords: [o.label],
          })),
        ],
        valueFn: (doc) => doc.categories,
      },
      author: {
        label: 'Author',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: authorOptions,
        valueFn: (doc) => doc.author?.reference ?? '',
      },
    }),
    [authorOptions, taskedForReview, signedDocuments]
  )

  const filteredDocuments = useFilteredData(
    activeDocuments,
    filterOptions,
    filters
  )

  const categoryFilteredDocuments = useMemo(() => {
    if (!selectedCategory || !filteredDocuments) {
      return filteredDocuments
    }
    return filteredDocuments.filter((doc) =>
      doc.categories.includes(selectedCategory)
    )
  }, [filteredDocuments, selectedCategory])

  /* MiniSearch-backed full-text search over the shared document field
   * config. `idleBuild: true` defers the ~450-600ms stemmed-index build
   * for the worst-case 609-doc patient to `requestIdleCallback` so first
   * paint stays responsive; the input shows a brief preparing state. */
  const {
    search: searchDocs,
    isIndexing,
    getExtractedFields,
  } = useClientSideSearch(categoryFilteredDocuments, {
    ...DOCUMENT_SEARCH_OPTIONS,
    extractFields: extractDocSearchFields,
    idleBuild: true,
  })

  const { items: documents, matchById } = useMemo(
    () => searchDocs(debouncedSearch),
    [searchDocs, debouncedSearch]
  )

  const isSearching = debouncedSearch.trim().length > 0

  // Seen Health Organization-author rows swap the avatar for the brand Logo.
  const { data: seenHealthOrg } = useSearchOne(
    ExtendedOrganization,
    ExtendedOrganization.querySeenHealthOrg()
  )
  const seenHealthOrgReference = seenHealthOrg?.reference.reference

  const columns = useMemo<ColumnDef<ExtendedDocumentReference>[]>(
    () => [
      {
        id: 'select',
        size: columnWidths.select,
        header: ({ table }) => (
          <Checkbox
            value={table.getIsAllPageRowsSelected()}
            onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            value={row.getIsSelected()}
            onChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: 'documentInfo',
        // No `size` — fills remaining width via DataTable's `auto` default.
        header: 'Document',
        enableSorting: false,
        cell: ({ row }) => (
          <DocumentInfoCell
            doc={row.original}
            match={matchById.get(row.original.id)}
            extractedFields={getExtractedFields(row.original.id)}
            activeCategoryFilter={selectedCategory}
            onTitleClick={() => maybeShowDocumentReference(row.original)}
            readableTitles={readableTitles}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        size: columnWidths.status,
        accessorFn: (docRef) =>
          documentStatusPriority.get(docRef.id) ?? STATUS_NONE,
        enableSorting: true,
        // Needs-review is the lowest priority value, so ascending puts it first.
        sortDescFirst: false,
        cell: ({ row }) => {
          const refString = getReferenceString(row.original.reference)
          const reviewTask = reviewTasksByDocument.get(refString)
          const signature = signedDocuments.get(refString)
          return (
            <div className="flex flex-col gap-1">
              {reviewTask && (
                <DocumentReviewTaskBadge task={reviewTask} variant="compact" />
              )}
              {signature && <SignedBadge signature={signature} />}
            </div>
          )
        },
      },
      {
        id: 'author',
        header: 'Added by',
        size: columnWidths.author,
        enableSorting: false,
        cell: ({ row }) => (
          <AuthorAvatarCell
            author={row.original.author}
            seenHealthOrgReference={seenHealthOrgReference}
          />
        ),
      },
      {
        id: 'serviceDate',
        header: 'Service date',
        size: columnWidths.serviceDate,
        accessorFn: (docRef) => docRef.serviceDateString,
        enableSorting: true,
        sortDescFirst: true,
        /* `serviceDateString` is genuinely undefined for documents with no
         * service date. TanStack's default flips undefined to the top when
         * descending; blanks belong last whichever way the column is sorted. */
        sortUndefined: 'last',
        cell: ({ row }) => (
          <DateDisplay value={row.original.serviceDateString} />
        ),
      },
      {
        id: 'date',
        header: 'Upload date',
        size: columnWidths.date,
        accessorFn: (docRef) => docRef.date,
        enableSorting: true,
        sortDescFirst: true,
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DateDisplay value={row.original.dateString} />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <DateTimeDisplay value={row.original.dateString} />
            </TooltipContent>
          </Tooltip>
        ),
      },
    ],
    [
      documentStatusPriority,
      reviewTasksByDocument,
      signedDocuments,
      matchById,
      getExtractedFields,
      selectedCategory,
      seenHealthOrgReference,
      maybeShowDocumentReference,
      columnWidths,
      readableTitles,
    ]
  )

  /* One list feeds both the chip's options and the `?sortBy=` contract, so an
   * id the URL names is always an id the table can actually render. */
  const sortableColumns = useMemo(() => getSortableColumns(columns), [columns])

  const sorting = useMemo<SortingState>(() => {
    const id = searchParams.get(SORT_BY_PARAM)
    const column = id ? sortableColumns.find((c) => c.id === id) : undefined
    /* An unrecognized id (a stale link from the old implementation) falls
     * through to the default rather than producing a sort no column can
     * render. */
    if (column) {
      const dir = searchParams.get(SORT_DIR_PARAM)
      return [
        { id: column.id, desc: dir ? dir === 'desc' : column.sortDescFirst },
      ]
    }
    if (keepTableSort) {
      /* Relevance is the search hook's own ordering, so it only means
       * something while searching. */
      return isSearching && id === RELEVANCE_SORT_BY ? [] : DEFAULT_SORTING
    }
    return isSearching ? [] : DEFAULT_SORTING
  }, [searchParams, isSearching, keepTableSort, sortableColumns])

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      const active = next[0]
      /* No `pageIndex` reset here — DataTable already owns that on every
       * sorting change. */
      updateSearchParams((params) => {
        if (active) {
          params.set(SORT_BY_PARAM, active.id)
          params.set(SORT_DIR_PARAM, active.desc ? 'desc' : 'asc')
        } else if (isSearching && keepTableSort) {
          /* An empty sort while searching has to be written: a missing
           * `sortBy` now means the table default, not relevance. */
          params.set(SORT_BY_PARAM, RELEVANCE_SORT_BY)
          params.delete(SORT_DIR_PARAM)
        } else {
          params.delete(SORT_BY_PARAM)
          params.delete(SORT_DIR_PARAM)
        }
      })
    },
    [sorting, isSearching, keepTableSort, updateSearchParams]
  )

  const moveToFolder = async (folder?: Folder): Promise<void> => {
    const selectedRows = Object.keys(rowSelection)
      .map((id) => filteredDocuments?.find((doc) => doc.id === id))
      .filter((doc): doc is ExtendedDocumentReference => !!doc)

    batchMutation.mutate(
      (batch) => {
        selectedRows.forEach((doc) => {
          batch.patch(doc, (d) => {
            d.folder = folder
          })
        })
      },
      {
        onSuccess: () => {
          setRowSelection({})
        },
      }
    )
  }

  const handleCategoryClick = useCallback(
    (category?: DocumentCategory): void => {
      if (category) {
        searchParams.set('category', category)
      } else {
        searchParams.delete('category')
      }
      searchParams.delete('folder')
      searchParams.set('pageIndex', '0')
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  const handleFolderClick = useCallback(
    (folderId?: string): void => {
      if (folderId) {
        searchParams.set('folder', folderId)
      } else {
        searchParams.delete('folder')
      }
      searchParams.delete('category')
      searchParams.set('pageIndex', '0')
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  const handleTabChange = useCallback(
    (value: string): void => {
      if (value === 'ALL') {
        handleCategoryClick(undefined)
      } else if (folders.some((f) => f.id === value)) {
        handleFolderClick(value)
      } else {
        handleCategoryClick(value as DocumentCategory)
      }
    },
    [folders, handleCategoryClick, handleFolderClick]
  )

  return (
    <div className="overflow-hidden">
      <div className="flex items-center gap-2 py-2">
        <div className="min-w-0 flex-1">
          <ResponsiveTabs
            value={selectedFolderId ?? selectedCategory ?? 'ALL'}
            onValueChange={handleTabChange}
          >
            <ResponsiveTabsList>
              <TabsTrigger value="ALL">All</TabsTrigger>
              {categoriesWithCounts.map((category) => {
                const reviewCount = categoryReviewCounts[category.value] ?? 0
                return (
                  <TabsTrigger key={category.value} value={category.value}>
                    {category.label}
                    {reviewCount > 0 && (
                      <Badge
                        variant="outline"
                        color="blue"
                        className="ml-1.5 min-w-5 justify-center"
                      >
                        {reviewCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                )
              })}
              {visibleFolders.map((folder) => (
                <TabsTrigger
                  key={folder.id}
                  value={folder.id}
                  className={cn(
                    selectedFolderId === folder.id &&
                      folder.id === BEHAVIORAL_HEALTH_FOLDER_ID &&
                      'bg-amber-100'
                  )}
                >
                  {folder.name}
                  {folderReviewCounts[folder.id] > 0 && (
                    <Badge
                      variant="default"
                      color="orange"
                      className="ml-1.5 h-5 min-w-5 px-1 text-xs"
                    >
                      {folderReviewCounts[folder.id]}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </ResponsiveTabsList>
          </ResponsiveTabs>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isIndexing && (
            <span className="text-xs text-gray-500">Preparing search…</span>
          )}
          {isSearching && !isIndexing && (
            <SearchSortChip
              columns={columns}
              sorting={sorting}
              onSortingChange={handleSortingChange}
            />
          )}
          <ShortcutSearchInput
            placeholder={isIndexing ? 'Preparing search…' : undefined}
            value={inputValue}
            onChange={setInputValue}
            className="w-48 shrink-0"
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 py-2">
        <FilterComposer<ExtendedDocumentReference>
          id="document-references-table-filters"
          filterOptions={filterOptions}
          onChange={setFilters}
        />
        {Object.keys(rowSelection).length > 0 && visibleFolders.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" loading={batchMutation.isPending}>
                <IconFolderPlus className="mr-2 size-4" />
                Move to Folder
                <IconChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => moveToFolder(undefined)}>
                Other
              </DropdownMenuItem>
              {visibleFolders.map((folder) => (
                <DropdownMenuItem
                  key={folder.id}
                  onClick={() => moveToFolder(folder)}
                >
                  {folder.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <DataTable
        columns={columns}
        data={documents}
        onRowClick={(row) => maybeShowDocumentReference(row)}
        enableRowSelection
        onRowSelectionChange={setRowSelection}
        enableSorting
        onSortingChange={handleSortingChange}
        state={{
          rowSelection,
          sorting,
          pagination: {
            pageIndex,
            pageSize,
          },
        }}
        getRowId={(row) => row.id}
        onPaginationChange={(updater) => {
          const next =
            typeof updater === 'function'
              ? updater({ pageIndex, pageSize })
              : updater
          updateSearchParams((params) => {
            params.set('pageIndex', next.pageIndex.toString())
            params.set('pageSize', next.pageSize.toString())
          })
        }}
      />
      <DocumentReferenceSheet
        documentReference={selectedDocument}
        open={!!selectedDocument}
        setOpen={(open) => {
          if (!open) {
            setSearchParams((prev) => {
              prev.delete('documentReferenceId')
              return prev
            })
          }
        }}
        hideParticipant
      />
    </div>
  )
}

function SignedBadge({ signature }: { signature: Signature }): JSX.Element {
  const { data: signer } = useRead(ExtendedPractitioner, signature.who)
  const displayName = signer?.name ?? referenceDisplay(signature.who)
  const rolesInitials = signer?.rolesInitials
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          color="green"
          className="flex w-fit items-center gap-1"
        >
          <Avatar profile={signature.who} size="icon" className="size-3" />
          Signed
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="flex flex-col gap-1">
          <span>
            Signed by {displayName}
            {rolesInitials && `, ${rolesInitials}`}
          </span>
          {signature.recorded && (
            <DateTimeDisplay
              value={signature.recorded}
              className="text-xs text-muted-foreground"
            />
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function AuthorAvatarCell({
  author,
  seenHealthOrgReference,
}: {
  author: ExtendedDocumentReference['author']
  seenHealthOrgReference: string | undefined
}): JSX.Element | null {
  if (isOfType(author, 'Practitioner')) {
    return <HoverPractitionerAvatar practitioner={author} />
  }
  if (isOfType(author, 'Organization')) {
    const isSeenHealth =
      !!seenHealthOrgReference && author.reference === seenHealthOrgReference
    return (
      <OrganizationAvatarHover
        organization={author}
        isSeenHealth={isSeenHealth}
      />
    )
  }
  return null
}

function OrganizationAvatarHover({
  organization: organizationReference,
  isSeenHealth,
}: {
  organization: Reference<Organization>
  isSeenHealth: boolean
}): JSX.Element {
  const { data: organization } = useRead(
    ExtendedOrganization,
    organizationReference
  )
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- row onClick is mouse-only; stopPropagation only matters for mouse; trigger wraps a presentational SVG/avatar; HoverCardTrigger handles keyboard. */}
        <span
          className="inline-flex cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          {isSeenHealth ? (
            <Logo className="size-7 rounded-full" announce />
          ) : (
            <Avatar profile={organizationReference} size={7} />
          )}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="bottom" align="start">
        <HoverOrganizationContent
          organization={organization ?? undefined}
          organizationReference={organizationReference}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
